import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Stack, Typography } from "@mui/material";

import { cloudflowTexts } from "../../../../assets/texts";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { type CloudFlowStatus } from "./type";

const STATUS_INDICATOR_COLORS = {
  default: {
    dark: "#A9E2FB",
    light: "#013654",
  },
  readyToPublish: {
    dark: "#BEE0BF",
    light: "#122E13",
  },
} as const;

type Props = {
  status: CloudFlowStatus;
  errorNodesCount?: number;
  onExpandClick: () => void;
  isExpanded: boolean;
};

const ErrorCountBadge = ({ count }: { count: number }) => (
  <Stack
    data-cy="cloudflow-error-count-badge"
    sx={{
      ml: 1,
      backgroundColor: "primary.main",
      borderRadius: "50%",
      width: 18,
      height: 18,
      p: 0.25,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Typography color="primary.contrastText" fontWeight={500} fontSize={12}>
      {count}
    </Typography>
  </Stack>
);

const ExpandIcon = ({ isExpanded }: { isExpanded: boolean }) => {
  const isDarkMode = useDarkThemeCheck();

  return (
    <Stack sx={{ pl: 0.5, display: "flex" }}>
      <ExpandMoreIcon
        data-cy="cloudflow-expand-icon"
        sx={{
          color: isDarkMode ? STATUS_INDICATOR_COLORS.default.dark : STATUS_INDICATOR_COLORS.default.light,
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </Stack>
  );
};

export const CloudFlowStatusIndicator = ({ status, onExpandClick, isExpanded, errorNodesCount = 0 }: Props) => {
  const isDarkMode = useDarkThemeCheck();

  const { default: defaultColor, readyToPublish } = STATUS_INDICATOR_COLORS;

  switch (status) {
    case "initial":
      return (
        <Typography
          variant="body1"
          color={isDarkMode ? defaultColor.dark : defaultColor.light}
          data-cy="cloudflow-initial-state"
        >
          {cloudflowTexts.STATUS.BUILD_YOUR_CLOUDFLOW}
        </Typography>
      );

    case "incomplete":
      return (
        <Stack
          alignItems="center"
          p={0}
          direction="row"
          onClick={onExpandClick}
          data-cy="cloudflow-incomplete-state"
          sx={{ cursor: "pointer" }}
        >
          <Typography variant="body1">{cloudflowTexts.STATUS.COMPLETE_YOUR_FLOW}</Typography>
          <ErrorCountBadge count={errorNodesCount} />
          <ExpandIcon isExpanded={isExpanded} />
        </Stack>
      );

    case "readyToPublish":
      return (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          data-cy="cloudflow-complete-state"
          color={isDarkMode ? readyToPublish.dark : readyToPublish.light}
        >
          <CheckIcon data-cy="cloudflow-success-icon" />
          <Typography variant="body1">{cloudflowTexts.STATUS.READY_TO_PUBLISH}</Typography>
        </Stack>
      );
    default:
      return null;
  }
};
